import React, { MouseEventHandler, useCallback, useContext, useMemo } from 'react'
import { rem } from 'polished'
import styled from 'styled-components'
import Pane from 'components/Common/Pane'
import { OfferPageActions } from 'contexts/OfferPage/offerPageStateReducer'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import { isAvailableForPurchase } from 'selectors/offerPage/offerPageSelectors'
import Group from 'components/utils/Group'
import ScrollAnchor from 'components/Common/ScrollAnchor'
import UrgencyLabel from 'LuxKit/Label/UrgencyLabel'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import TextLink from 'LuxKit/TextLink'
import TextButton from 'LuxKit/Button/TextButton'
import MessageBanner from 'LuxKit/Banners/MessageBanner'
import TripGuard from 'tripPlanner/components/TripGuard/TripGuard'
import LineExclamationCircleIcon from 'LuxKit/Icons/line/LineExclamationCircleIcon'
import BookmarkButton from 'tripPlanner/components/Bookmark/BookmarkButton/BookmarkButton'
import AccommodationRoomTile from 'components/AccommodationKit/AccommodationRoomTile'
import AccommodationRoomTileImage from 'components/AccommodationKit/AccommodationRoomTileImage'
import AccommodationRoomTileDetailsVariant from './AccommodationRoomTileDetailsVariant'
import FlashHotelRateOptionVariant from './FlashHotelRateOptionVariant'
import FlashHotelCheapestRateOptionVariant from './FlashHotelCheapestRateOptionVariant'
import ModalContext from 'contexts/ModalContext'
import FlashHotelRoomDetailsModal from './Modals/FlashHotelRoomDetailsModal'
import WalledContent from 'components/Common/WalledContent'
import AccountAccessModal from 'components/Account/AccountAccess/AccountAccessModal'
import BookingsCountLabel from 'components/Common/Labels/BookingsCountLabel'
import RoomsRemainingLabel from 'components/Common/Labels/RoomsRemainingLabel'
import LoyaltyStayUpgradeBenefitMessageBanner from 'loyalty/components/LoyaltyStayUpgradeBenefitMessageBanner'
import OfferUrgencyRoomCount from 'components/Common/Labels/OfferUrgencyRoomCount'
import useUserHasLoyaltyBenefit from 'loyalty/hooks/useUserHasLoyaltyBenefit'
import LineInfoCircleIcon from 'LuxKit/Icons/line/LineInfoCircleIcon'
import ContactContext from 'contexts/contactContext'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import { OptimizelyExperiments } from 'constants/optimizely'
import useOptimizelyExperiment from 'hooks/Optimizely/useOptimizelyExperiment'
import { showFlexibleCancellation, showFreeCancellation } from 'components/Common/CancellationPolicies/Flash/FlashCancellationPolicies'
import { getDynamicPricingUrgencyLabelData } from 'components/OfferPage/utils'
import { useAppSelector } from 'hooks/reduxHooks'
import { getOptimizelyExperimentVariation } from 'lib/optimizely/optimizelyUtils'
import { useInView } from 'react-intersection-observer'
import config from 'constants/config'
import { useUrgentOfferRoomDateData } from 'hooks/Offers/useUrgentOfferRoomDateData'

const AccommodationPackageTileContainer = styled.div`
  padding: ${rem(16)} ${rem(20)} ${rem(20)};
`

interface Props {
  offer: App.HotelOffer
  packageViews: Array<App.OfferPackageOptionView>
  checkIn?: string
  roomTypeId?: string
  rooms: Array<App.Occupants> | undefined
  detailsModalMode: 'room' | 'room-and-package'
  availableRoomCount?: number
  availableForPurchase?: boolean
  useSkiDealAvailabilityActions?: boolean
  airports?: Array<App.Airport>
}

function FlashHotelRoomOptionVariant(props: Props) {
  const {
    offer,
    packageViews,
    checkIn,
    roomTypeId,
    availableRoomCount,
    detailsModalMode,
    availableForPurchase,
    useSkiDealAvailabilityActions,
    airports,
  } = props

  const pkg = packageViews[0].package
  const contactContext = useContext(ContactContext)

  const [roomFitsCapacity, datesAvailable] = useMemo(() => [
    packageViews.some(view => view.capacityValid),
    packageViews.some(view => view.hotelAvailable),
  ], [packageViews])

  const cheapestPackageView = useMemo(() => packageViews.reduce((cheapest, current) => current.hotelPrice < cheapest.hotelPrice ? current : cheapest), [packageViews])
  const destinationAirport = useMemo(() => airports?.find(({ code }) => code === offer.flightDestinationPort), [airports, offer.flightDestinationPort])

  const isHorizontalSliderEnabled = !!useOptimizelyExperiment(OptimizelyExperiments.croHorizontalScrollFlashPackages)

  const cachedRoomDateData = useUrgentOfferRoomDateData(offer.id, roomTypeId)
  const liveRoomsBeforeSurcharge = useMemo(() => getDynamicPricingUrgencyLabelData(cachedRoomDateData, packageViews[0].availableRooms), [cachedRoomDateData, packageViews])
  const urgencyTagAbTestVariation = useAppSelector(state => getOptimizelyExperimentVariation(state, OptimizelyExperiments.lereDynamicPricingUrgencyTag))
  const [sectionRef, sectionInView] = useInView({ triggerOnce: false, threshold: 0.5 })
  const canTriggerExperiment = useMemo(() => {
    return sectionInView && !!liveRoomsBeforeSurcharge && config.BRAND === 'luxuryescapes'
  }, [sectionInView, liveRoomsBeforeSurcharge])
  useOptimizelyExperiment(OptimizelyExperiments.lereDynamicPricingUrgencyTag, canTriggerExperiment)

  const [experimentHideRoomsRemainingLabel, experimentRoomsBeforeSurcharge] = useMemo(() => {
    if (urgencyTagAbTestVariation === 'on') {
      return [!!liveRoomsBeforeSurcharge, liveRoomsBeforeSurcharge]
    }
    return [false, undefined]
  }, [urgencyTagAbTestVariation, liveRoomsBeforeSurcharge])

  const hasMultipleCancellationPolicies = useMemo(() => {
    const firstPackageFreeCancellation = showFreeCancellation(checkIn, false, packageViews[0].package.roomRate?.cancellationPolicy)
    const firstPackageFlexibleCancellation = showFlexibleCancellation(checkIn, false, packageViews[0].package.roomRate?.cancellationPolicy)
    const firstPackageRefundable = firstPackageFreeCancellation || firstPackageFlexibleCancellation

    return !packageViews.every(view => {
      const freeCancellation = showFreeCancellation(checkIn, false, view.package.roomRate?.cancellationPolicy)
      const flexibleCancellation = showFlexibleCancellation(checkIn, false, view.package.roomRate?.cancellationPolicy)
      const refundable = freeCancellation || flexibleCancellation

      return refundable === firstPackageRefundable
    })
  }, [packageViews, checkIn])

  const offerPageDispatch = useOfferPageDispatch()

  const handleOpenCalendar = useCallback<MouseEventHandler<HTMLAnchorElement>>(() => {
    offerPageDispatch({ type: OfferPageActions.setRoomType, room: pkg.roomType })
    offerPageDispatch({ type: OfferPageActions.toggleFilter, filter: 'checkin' })
  }, [offerPageDispatch, pkg.roomType])

  const openCapcityFilter = useCallback(() => {
    offerPageDispatch({ type: OfferPageActions.setRoomType, room: pkg.roomType })
    offerPageDispatch({ type: OfferPageActions.toggleFilter, filter: 'capacity' })
  }, [offerPageDispatch, pkg.roomType])

  const showModal = useContext(ModalContext)
  const onShowRoomDetails = useCallback(() => {
    if (pkg.roomType) {
      showModal(<FlashHotelRoomDetailsModal room={pkg.roomType} />)
    }
  }, [pkg.roomType, showModal])

  const onSignUpClick = useCallback(() => {
    showModal(<AccountAccessModal />)
  }, [showModal])

  const userHasRoomUpgradesAvailable = useUserHasLoyaltyBenefit('hotel_upgrade')

  const croPopularOptionBookingRemaining = !!useOptimizelyExperiment(OptimizelyExperiments.croPopularOptionBookingRemaining, !!availableRoomCount && !!checkIn)

  return (
    <Pane ref={sectionRef} border="neutral-five" borderRadius="S" data-testid="package-option">
      <ScrollAnchor id={pkg.roomType.id} vOffset={rem(80)} />
      <AccommodationRoomTile roomPackage={pkg}>
        <AccommodationRoomTileImage
          roomPackage={pkg}
        />
        <AccommodationRoomTileDetailsVariant
          roomPackage={pkg}
          onViewRoomDetails={isHorizontalSliderEnabled ? onShowRoomDetails : undefined}
          urgencyLabels={<>
            {!datesAvailable && <UrgencyLabel variant="critical2">{useSkiDealAvailabilityActions ? `CALL US - ${contactContext.local.humanReadable}` : 'SOLD OUT'}</UrgencyLabel>}
            {checkIn && !croPopularOptionBookingRemaining && !experimentHideRoomsRemainingLabel && <RoomsRemainingLabel roomCount={cheapestPackageView.availableRooms ?? 0} threshold="rate" />}
            {checkIn && croPopularOptionBookingRemaining && !experimentHideRoomsRemainingLabel && availableRoomCount && <OfferUrgencyRoomCount availableRoomCount={availableRoomCount} />}
            <BookingsCountLabel bookings={pkg.roomType.stats.booked} threshold="room" />
          </>}
          actions={availableForPurchase && datesAvailable && roomFitsCapacity && <CSSBreakpoint min="desktop">
            <TripGuard>
              <BookmarkButton offer={offer} roomTypeId={pkg.roomType.id} />
            </TripGuard>
          </CSSBreakpoint>}
        />
      </AccommodationRoomTile>
      <WalledContent
        enforced={offer.walledGarden}
        wall={(
          <Group fullHeight direction="vertical" verticalAlign="end">
            <TextButton size="large" kind="secondary" onClick={onSignUpClick}>Sign up to view exclusive rates</TextButton>
          </Group>
        )}
      >
        {datesAvailable && roomFitsCapacity && (
          <VerticalSpacer gap={12}>
            <AccommodationPackageTileContainer>
              <Group direction="vertical" gap={8} noDisplayWhenEmpty>
                {packageViews[0].loyaltyUpgradeAvailability && userHasRoomUpgradesAvailable && <LoyaltyStayUpgradeBenefitMessageBanner offer={offer} />}
                {isHorizontalSliderEnabled && (
                  <FlashHotelRateOptionVariant
                    offer={offer}
                    roomsBeforeSurcharge={experimentRoomsBeforeSurcharge}
                    optionNumber={1}
                    packageView={cheapestPackageView}
                    showOptionLabel={false}
                    flightDestination={destinationAirport}
                  />
                )}
                {!isHorizontalSliderEnabled && (
                  <FlashHotelCheapestRateOptionVariant
                    key={cheapestPackageView.package.uniqueKey}
                    offer={offer}
                    roomsBeforeSurcharge={experimentRoomsBeforeSurcharge}
                    packageView={cheapestPackageView}
                    detailsModalMode={detailsModalMode}
                    hasMultipleCancellationPolicies={hasMultipleCancellationPolicies}
                  />
                )}
              </Group>
            </AccommodationPackageTileContainer>
          </VerticalSpacer>
        )}
      </WalledContent>
      {(!datesAvailable || !roomFitsCapacity) && (
        <AccommodationPackageTileContainer>
          <Group direction="vertical" gap={8}>
            {!roomFitsCapacity && <MessageBanner
              kind="informational-primary"
              icon={<LineExclamationCircleIcon />}
              heading="This room cannot accommodate your number of guests"
            >
              Choose another room or <TextLink size="medium" weight="regular" onClick={openCapcityFilter}>add more rooms</TextLink>
            </MessageBanner>}
            {!useSkiDealAvailabilityActions && roomFitsCapacity && !datesAvailable && <MessageBanner
              kind="informational-primary"
              icon={<LineExclamationCircleIcon />}
              heading="This room is sold out on your dates or max number of guests exceeded"
            >
              Choose another room or <TextLink size="medium" weight="regular" onClick={handleOpenCalendar}>try different dates</TextLink>
            </MessageBanner>}
            {useSkiDealAvailabilityActions && roomFitsCapacity && !datesAvailable && <MessageBanner
              kind="informational-primary"
              icon={<LineInfoCircleIcon />}
            >
              Call us for last minute availability on <TextLink href={`tel:${contactContext.local.number}`}>{contactContext.local.humanReadable}</TextLink>
            </MessageBanner>}
            {!isHorizontalSliderEnabled && (
              <TextButton kind="secondary" size="large" onClick={onShowRoomDetails}>
                View details
              </TextButton>
            )}
          </Group>
        </AccommodationPackageTileContainer>
      )}
    </Pane>
  )
}

export default withOfferPageAndReduxState((state, appState) => ({
  checkIn: state.checkInDate,
  rooms: state.rooms,
  availableForPurchase: isAvailableForPurchase(state, appState),
  airports: appState.geo.airports,
}), FlashHotelRoomOptionVariant)
